import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowLeft, Cable, Disc3, Smartphone, Settings2, CheckCircle2 } from "lucide-react";

const STEPS = [
  { icon: Cable, title: "Brancher l'imprimante", text: "Raccorder la Brother QL-820NWBc en Ethernet au réseau local (LAN) et lui attribuer une adresse IP fixe sur la box / le routeur." },
  { icon: Disc3, title: "Charger le rouleau", text: "Installer un rouleau DK-11202 (étiquettes prédécoupées 62 × 100 mm, blanches, thermique direct 300 dpi, monochrome)." },
  { icon: Smartphone, title: "Installer Brother iPrint&Label sur la tablette", text: "Depuis l'App Store (iPad / iPhone) ou Google Play (Android) : installer, ouvrir, ajouter la QL-820NWBc détectée sur le réseau, puis régler le média sur DK-11202 62 mm." },
  { icon: Settings2, title: "Laisser le mode impression sur « Auto »", text: "Dans Admin → Réglages → Impression étiquettes : le mode Auto envoie l'étiquette vers iPrint&Label sur tablette, et ouvre le PDF dans la boîte d'impression système sur ordinateur." },
  { icon: CheckCircle2, title: "Tester", text: "Utiliser le bouton « Imprimer une étiquette de test » dans les réglages : une étiquette 62 × 100 mm doit sortir de l'imprimante sans aucune confirmation intermédiaire." },
];

export default function AideImprimante() {
  const { user } = useAuth();
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="aide-imprimante">
      <Link to={user ? "/dashboard" : "/login"} className="inline-flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Aide</div>
        <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900 flex items-center gap-3">
          <Printer className="w-8 h-8" /> Configuration imprimante
        </h1>
        <p className="text-slate-600 font-medium mt-1">
          Brother QL-820NWBc · étiquettes 62 × 100 mm · impression 1 tap depuis la tablette.
        </p>
      </div>

      <ol className="space-y-4">
        {STEPS.map((s, i) => (
          <li key={s.title} data-testid={`aide-step-${i + 1}`} className="rounded-2xl bg-white border-2 border-slate-200 p-5 flex gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center shrink-0 font-heading font-black text-xl">
              {i + 1}
            </div>
            <div>
              <div className="font-heading font-black text-lg uppercase tracking-tight text-slate-900 flex items-center gap-2">
                <s.icon className="w-5 h-5 text-slate-500" /> {s.title}
              </div>
              <p className="text-slate-600 text-sm mt-1">{s.text}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="rounded-2xl border-2 border-dashed border-slate-300 p-5 text-sm text-slate-600 space-y-1">
        <div className="font-bold text-slate-800 uppercase text-xs tracking-wide">Comment ça marche</div>
        <p>L'application génère un PDF au format exact 62 × 100 mm (une étiquette par page, noir sur blanc) et le confie au canal d'impression du terminal :</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li><b>iPad / iPhone / Android</b> : ouverture automatique de Brother iPrint&Label via <code className="font-mono text-xs bg-slate-100 px-1 rounded">brotherprint://</code>, impression, retour au navigateur.</li>
          <li><b>Ordinateur / Chromebook</b> : ouverture du PDF dans un nouvel onglet et boîte d'impression système.</li>
        </ul>
        <p className="text-xs text-slate-400">Le lien PDF remis à l'imprimante est public et expire au bout de 15 minutes.</p>
      </div>
    </div>
  );
}
